import {
  ArgumentError,
  InvalidInputError,
  UnknownApiError,
} from './errors';
import { isZeroOrPositiveInteger } from './utils';

export default function (api) {
  /**
   * @typedef {Object} CategoryNode
   * @property {string} id
   * @property {Array.<Object>} items
   */

  /**
   * @memberof API
   * @instance
   */
  const browse = {
    /**
     * Retrieve a category node.
     * Auth token is not required.
     *
     * @method
     * @this API
     *
     * @param {Object} payload
     * @param {string} payload.categoryId
     * @param {number=} payload.limit
     * @param {number=} payload.offset
     *
     * @return {Promise} promise
     * @fulfill {CategoryNode}
     * @reject {ArgumentError} Category ID is required
     * @reject {InvalidInputError} Category cannot be found
     * @reject {UnknownApiError} Server returned with unexpected status code
    */
    getCategory: function getCategory(payload = {}) {
      if (typeof payload.categoryId === 'undefined' || typeof payload.categoryId !== 'string') {
        return Promise.reject(new ArgumentError('payload.categoryId is required and must be a string'));
      }

      if (typeof payload.limit !== 'undefined' && !isZeroOrPositiveInteger(payload.limit)) {
        return Promise.reject(new ArgumentError('payload.limit must be a positive integer'));
      }

      if (typeof payload.offset !== 'undefined' && !isZeroOrPositiveInteger(payload.offset)) {
        return Promise.reject(new ArgumentError('payload.offset must be an integer'));
      }

      return this.request('get', `/nibble/v1/category/${payload.categoryId}`, {
        query: {
          size: payload.limit,
          from: payload.offset,
        },
      })
        .then((res) => {
          switch (res.status) {
            case 200: {
              return res.body;
            }
            case 404: {
              throw new InvalidInputError('Category not found', res);
            }
            default: {
              throw new UnknownApiError('server returned with unhandled status code', res);
            }
          }
        });
    }.bind(api),

    /**
     * @typedef Show
     * @property {number} episodes
     * @property {string} episodesHuman - Human readable representation, ie '16 episodes'
     * @property {string} id
     * @property {Object} images
     * @property {string} kind - Should always be 'show'
     * @property {Season[]} seasons
     * @property {string} title
     * @property {string} url
     */

    /**
     * @typedef Season
     * @property {number} seasonNumber
     * @property {string} seasonTitle
     * @property {Episode[]} episodes
     * */

    /**
     * @typedef Episode
     * @property {string} assetId
     * @property {string} description
     * @property {number} duration
     * @property {string} durationHuman - Ex. '42 min'
     * @property {string} episodeNumber
     * @property {string} id
     * @property {Object} images
     * @property {string} kind - Should always be 'episode'
     * @property {string} title
     * @property {string} url
     * */

    /**
     * Get a show. A show contains information about seasons and episodes. Use getViewable to
     * get an episode of a show.
     * Auth token is not required.
     *
     * @method
     * @this API
     *
     * @param {Object} payload
     * @param {string} payload.showId
     *
     * @return {Promise} promise
     * @fulfill {Show}
     * @reject {ArgumentError} Show ID is required
     * @reject {InvalidInputError} Show cannot be found
     * @reject {UnknownApiError} Server returned with unexpected status code
    * */
    getShow: function getShow(payload = {}) {
      if (typeof payload.showId === 'undefined' || typeof payload.showId !== 'string') {
        return Promise.reject(new ArgumentError('payload.showId is required and must be a string'));
      }

      return this.request('get', `/nibble/v1/show/${payload.showId}`)
        .then((res) => {
          switch (res.status) {
            case 200: {
              return res.body;
            }
            case 404: {
              throw new InvalidInputError('Show not found', res);
            }
            default: {
              throw new UnknownApiError('server returned with unhandled status code', res);
            }
          }
        });
    }.bind(api),

    /**
     * @typedef Viewable
     * @property {string} assetId
     * @property {CatchUp} episodesHuman - Human readable representation, ie '16 episodes'
     * @property {string} description
     * @property {number} duration - Duration in seconds
     * @property {string} durationHuman - Ex. '89 min'
     * @property {number} expires - Unix timestamp in 13-digit
     * @property {string} expiresHuman - Ex. '5 jours'
     * @property {string[]} genres
     * @property {string} id
     * @property {Object} images
     * @property {string} kind
     * @property {string} title
     * @property {string} url
     */

    /**
     * @typedef CatchUp
     * @property {number} channelAiredTimestamp
     * @property {string} channelId
     * @property {string} channelLogo
     * @property {string} channelLogoDark
     * @property {string} channelName
     * */

    /**
     * Get a viewable. For example an episodes of a show or a movie.
     * Auth token is not required.
     *
     * @method
     * @this API
     *
     * @param {Object} payload
     * @param {string} payload.viewableId
     *
     * @return {Promise} promise
     * @fulfill {Viewable}
     * @reject {ArgumentError} Viewable ID is required
     * @reject {InvalidInputError} Viewable cannot be found
     * @reject {UnknownApiError} Server returned with unexpected status code
    * */
    getViewable: function getViewable(payload = {}) {
      if (typeof payload.viewableId === 'undefined' || typeof payload.viewableId !== 'string') {
        return Promise.reject(new ArgumentError('payload.viewableId is required and must be a string'));
      }

      return this.request('get', `/nibble/v1/viewable/${payload.viewableId}`)
        .then((res) => {
          switch (res.status) {
            case 200: {
              return res.body;
            }
            case 404: {
              throw new InvalidInputError('Viewable not found', res);
            }
            default: {
              throw new UnknownApiError('server returned with unhandled status code', res);
            }
          }
        });
    }.bind(api),

    /**
     * This method provides a way to get non-standard metadata about a viewable.
     * All metadata that has been supplied for the viewable will be returned
     * whereas getViewable() only returns the standard fields.
     *
     * Auth token is not required.
     *
     * @method
     * @this API
     *
     * @param {Object} payload
     * @param {string} payload.viewableId
     *
     * @return {Promise} promise
     * @fulfill {Object}
     * @reject {ArgumentError} Viewable ID is required
     * @reject {InvalidInputError} Viewable cannot be found
     * @reject {UnknownApiError} Server returned with unexpected status code
     * */
    getViewableRaw: function getViewableRaw(payload = {}) {
      if (typeof payload.viewableId === 'undefined' || typeof payload.viewableId !== 'string') {
        return Promise.reject(new ArgumentError('payload.viewableId is required and must be a string'));
      }

      return this.request('get', `/nibble/v1/query?q=_id:${payload.viewableId}`)
        .then((res) => {
          switch (res.status) {
            case 200: {
              if (res.body.items.length === 0) {
                throw new InvalidInputError('Viewable not found', res);
              }

              return res.body.items[0];
            }
            case 404: {
              throw new InvalidInputError('Viewable not found', res);
            }
            default: {
              throw new UnknownApiError('server returned with unhandled status code', res);
            }
          }
        });
    }.bind(api),

    /**
     * @typedef SearchResult
     * @type Object
     * @property {Asset[]} items
     * */

    /**
     * @typedef Asset
     * @type Object
     * @property {string} assetId
     * @property {string} description
     * @property {number} duration - Duration in seconds
     * @property {string} durationHuman - Ex. '89 min'
     * @property {string} id
     * @property {Object} images
     * @property {string} kind
     * @property {string} title
     * @property {string} url
     * */

    /**
     * Search using free text.
     * Auth token not required.
     *
     * @method
     * @this API
     *
     * @param {Object} payload
     * @param {string} payload.query
     *
     * @return {Promise} promise
     * @fulfill {SearchResult}
     * @reject {ArgumentError} Query is required and must be a string
     * @reject {UnknownApiError} Server returned with unexpected status code
     * */
    search: function search(payload = {}) {
      if (typeof payload.query === 'undefined' || typeof payload.query !== 'string') {
        return Promise.reject(new ArgumentError('payload.query is required and must be a string'));
      }

      return this.request('get', '/nibble/v1/search', {
        query: { q: payload.query },
      })
        .then((res) => {
          switch (res.status) {
            case 200: {
              return res.body;
            }
            default: {
              throw new UnknownApiError('server returned with unhandled status code', res);
            }
          }
        });
    }.bind(api),

    /**
     * @typedef QueryResult
     * @type Object
     * @property {Object[]} items
     * */

    /**
     * This method provides a way to query viewables by simple query logic.
     *
     * Auth token is not required.
     *
     * @method
     * @this API
     *
     * @param {Object} payload
     * @param {string} payload.viewableId
     *
     * @return {Promise} promise
     * @fulfill {QueryResult}
     * @reject {ArgumentError} Query is required and must be a string
     * @reject {ArgumentError} Sort must be a string
     * @reject {UnknownApiError} Server returned with unexpected status code
     * */
    query: function query(payload = {}) {
      if (typeof payload.query === 'undefined' || typeof payload.query !== 'string') {
        return Promise.reject(new ArgumentError('payload.query is required and must be a string'));
      }

      if (typeof payload.sort !== 'undefined' && typeof payload.sort !== 'string') {
        return Promise.reject(new ArgumentError('payload.sort must be a string'));
      }

      return this.request('get', '/nibble/v1/query', {
        query: {
          q: payload.query,
          sort: payload.sort,
        },
      })
        .then((res) => {
          switch (res.status) {
            case 200: {
              return res.body;
            }
            default: {
              throw new UnknownApiError('server returned with unhandled status code', res);
            }
          }
        });
    }.bind(api),
  };

  return browse;
}
